"use client";

import { useActionState, useCallback, useEffect } from "react";

import { verifyOtp } from "@/app/(portal)/(unauthenticated)/(forms)/actions";
import { VerificationMethod } from "@/app/(portal)/(unauthenticated)/(forms)/types";
import { maskPhone } from "@/components/lib/masks";
import ApplicationSectionHeader from "@/components/ui/application-section-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/hooks/use-toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

export default function OtpVerificationForm({
  method,
  recipient,
  onClickHelp,
  onConfirmed,
  onError,
}: {
  method: VerificationMethod;
  recipient: string;
  onClickHelp: () => void;
  onConfirmed?: () => void;
  onError?: (error: string) => void;
}) {
  const { toast } = useToast();

  const action = useCallback(
    (_prevState: unknown, data: FormData) => {
      const code = data.get("code");
      if (!code) {
        return { error: "Please enter a code" };
      }
      return verifyOtp({
        method,
        recipient,
        code: code as string,
      }).then((res) => {
        if ("error" in res) return { error: res.error };
        onConfirmed?.();
        return { error: "" };
      });
    },
    [method, recipient, onConfirmed]
  );

  const [state, formAction, pending] = useActionState<
    { error?: string },
    FormData
  >(action, { error: "" });

  useEffect(() => {
    if (state.error) {
      toast({
        title: state.error,
        variant: "destructive",
      });
      onError?.(state.error);
    }
  }, [state.error, toast, onError]);

  const communicationType = method === "sms" ? "a text message" : "an email";

  const communicationMechanism =
    method === "sms" ? "phone number" : "email address";

  const formattedRecipient =
    method === "sms" ? maskPhone(recipient) : recipient;

  return (
    <>
      <ApplicationSectionHeader
        title="Enter your code"
        description={
          <>
            <span>{`We sent ${communicationType} containing a verification code to the ${communicationMechanism} `}</span>
            <span className="font-normal text-[#4b4b55]">
              {formattedRecipient}
            </span>
          </>
        }
      />
      <form action={formAction} className="flex w-full flex-col gap-6">
        <InputOTP maxLength={6} autoFocus name="code" data-testid="otp-input">
          <InputOTPGroup className="w-full justify-between">
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={0}
            />
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={1}
            />
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={2}
            />
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={3}
            />
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={4}
            />
            <InputOTPSlot
              className="rounded-containers h-14 w-12 border text-3xl"
              index={5}
            />
          </InputOTPGroup>
        </InputOTP>
        <Button
          data-testid="continue-button"
          type="submit"
          className="h-12 w-full rounded-xl px-5 py-3 text-sm font-normal"
          disabled={pending}
        >
          Continue
        </Button>
      </form>
      <Button
        variant="link"
        onClick={onClickHelp}
        className="m-0 justify-start p-0 text-left text-sm leading-[150%] font-light text-[#595FEF] [text-decoration:underline]"
      >
        Having Trouble?
      </Button>
    </>
  );
}
